import { useEffect, useState } from "react";
import { Award, Bot, CalendarClock, Crown, ExternalLink, FileCheck2, FileText, Gift, ImageIcon, LoaderCircle, Map, StickyNote, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_ROLES, can } from "@/lib/adminRoles";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_META = {
  completed: { label: "Terminé", cls: "bg-emerald-100 text-emerald-700" },
  in_progress: { label: "En cours", cls: "bg-amber-100 text-amber-700" },
  upcoming: { label: "À venir", cls: "bg-slate-100 text-slate-500" },
};
const AGENT_LABELS = { coach: "Coach civique", demarches: "Assistant démarches", interprete: "Interprète" };
const OBJECTIVES = { naturalisation: "Naturalisation", carte_resident: "Carte de résident", renouvellement: "Renouvellement de titre" };

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-right text-xs font-semibold text-slate-700">{value}</span>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="rounded-xl border border-slate-100 p-4" data-testid={testid}>
      <h3 className="flex items-center gap-2 text-xs font-bold text-slate-900">
        <Icon className="h-3.5 w-3.5 text-bleu" /> {title}
      </h3>
      <div className="mt-2">{children}</div>
    </section>
  );
}

export function AdminUserSheet({ userId, onClose }) {
  const { user: me } = useAuth();
  const [d, setD] = useState(null);
  const [customDays, setCustomDays] = useState("");
  const [granting, setGranting] = useState(false);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.put(`/admin/users/${userId}/note`, { note });
      toast.success("Note enregistrée.");
    } catch {
      toast.error("Impossible d'enregistrer la note.");
    } finally {
      setSavingNote(false);
    }
  };

  const loadDetails = (id) => api.get(`/admin/users/${id}/details`).then((r) => {
    setD(r.data);
    setNote(r.data.profile.admin_note || "");
  });

  useEffect(() => {
    if (!userId) return;
    setD(null);
    loadDetails(userId).catch(() => { toast.error("Impossible de charger la fiche utilisateur."); onClose(); });
  }, [userId, onClose]);

  const grant = async (days) => {
    if (!days || days < 1 || days > 365) { toast.error("Indiquez entre 1 et 365 jours."); return; }
    setGranting(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/grant-premium`, { days });
      toast.success(data.message);
      setCustomDays("");
      await loadDetails(userId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'octroyer le crédit.");
    } finally {
      setGranting(false);
    }
  };

  const fmtDate = (iso, opts) => iso ? new Date(iso).toLocaleDateString("fr-FR", opts) : null;

  return (
    <Sheet open={!!userId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="admin-user-sheet">
        {!d ? (
          <div className="flex h-40 items-center justify-center"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>
        ) : (
          <>
            <SheetHeader className="text-left">
              <SheetTitle className="flex flex-wrap items-center gap-2" data-testid="admin-user-sheet-name">
                {d.profile.name || d.profile.email}
                {d.subscription.premium_effective && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">Premium</span>}
                {d.profile.suspended && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-600">Suspendu</span>}
              </SheetTitle>
              <SheetDescription>{d.profile.email}{d.profile.created_at && ` · inscrit le ${fmtDate(d.profile.created_at)}`}</SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-3">
              <Section icon={StickyNote} title="Note interne (visible uniquement des admins)" testid="admin-user-sheet-note">
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={4000}
                  placeholder="Suivi, contexte, points d'attention sur cet utilisateur…"
                  className="w-full rounded-xl border border-slate-200 bg-amber-50/40 px-3 py-2.5 text-xs focus:border-bleu focus:outline-none"
                  data-testid="admin-user-note-input" />
                <button onClick={saveNote} disabled={savingNote}
                  className="mt-1 rounded-full bg-bleu px-4 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-bleu-dark disabled:opacity-50"
                  data-testid="admin-user-note-save">
                  {savingNote ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Enregistrer la note"}
                </button>
              </Section>

              <Section icon={UserRound} title="Profil" testid="admin-user-sheet-profile">
                <Row label="Téléphone" value={d.profile.phone} />
                <Row label="Adresse" value={d.profile.address} />
                <Row label="Ville" value={[d.profile.postal_code, d.profile.city].filter(Boolean).join(" ")} />
                <Row label="Département" value={d.profile.departement?.code && `${d.profile.departement.nom || ""} (${d.profile.departement.code})`.trim()} />
                <Row label="Préfecture" value={d.profile.departement?.prefecture} />
                <Row label="Objectif" value={OBJECTIVES[d.profile.objective] || d.profile.objective} />
                <Row label="Langue préférée" value={d.profile.preferred_lang} />
                <Row label="Titre de séjour" value={d.profile.titre_type} />
                <Row label="Expiration du titre" value={fmtDate(d.profile.titre_expiry)} />
              </Section>

              <Section icon={Crown} title="Abonnement" testid="admin-user-sheet-subscription">
                <Row label="Formule" value={d.subscription.premium_effective ? "Premium" : "Gratuite"} />
                <Row label="Premium jusqu'au" value={fmtDate(d.subscription.premium_until)} />
                <Row label="Abonnement Stripe" value={d.subscription.stripe ? "Oui" : null} />
                <Row label="Code parrainage" value={d.subscription.referral_code} />
              </Section>

              {!ADMIN_ROLES.includes(d.profile.role) && can(me, "premium") && (
                <Section icon={Gift} title="Octroyer un crédit de connexion (Premium offert)" testid="admin-user-sheet-grant">
                  <div className="flex flex-wrap gap-2">
                    {[{ l: "+1 jour", v: 1 }, { l: "+1 semaine", v: 7 }, { l: "+1 mois", v: 30 }, { l: "+3 mois", v: 90 }].map((o) => (
                      <button key={o.v} disabled={granting} onClick={() => grant(o.v)}
                        className="rounded-full border border-amber-200 bg-amber-50 px-3.5 py-1.5 text-[11px] font-bold text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
                        data-testid={`admin-grant-${o.v}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2.5 flex items-center gap-2">
                    <input type="number" min={1} max={365} value={customDays} onChange={(e) => setCustomDays(e.target.value)}
                      placeholder="Nb de jours (1-365)"
                      className="w-40 rounded-xl border border-slate-200 px-3 py-2 text-xs focus:border-bleu focus:outline-none"
                      data-testid="admin-grant-custom-input" />
                    <button disabled={granting || !customDays} onClick={() => grant(parseInt(customDays, 10))}
                      className="rounded-full bg-bleu px-4 py-2 text-[11px] font-bold text-white transition-colors hover:bg-bleu-dark disabled:opacity-50"
                      data-testid="admin-grant-custom-button">
                      {granting ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Offrir"}
                    </button>
                  </div>
                  <p className="mt-2 text-[10px] text-slate-400">Le crédit prolonge l'accès Premium (IA illimitée, examens blancs) et s'ajoute à la durée restante. Action tracée dans le journal.</p>
                </Section>
              )}

              <Section icon={Map} title={`Progression du parcours — ${d.progression.percent} %`} testid="admin-user-sheet-progression">
                <Progress value={d.progression.percent} className="h-2" />
                <ul className="mt-3 space-y-1.5">
                  {d.progression.steps.map((s) => {
                    const m = STEP_META[s.status] || STEP_META.upcoming;
                    return (
                      <li key={s.title} className="flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-600">{s.title}</span>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${m.cls}`}>{m.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </Section>

              <Section icon={FileCheck2} title={`Documents — ${d.documents.checked}/${d.documents.total} obtenus`} testid="admin-user-sheet-documents">
                <ul className="space-y-1">
                  {d.documents.items.map((doc) => (
                    <li key={doc.label} className="flex items-center gap-2 text-xs">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${doc.checked ? "bg-emeraude" : "bg-slate-200"}`} />
                      <span className={doc.checked ? "text-slate-700" : "text-slate-400"}>{doc.label}</span>
                    </li>
                  ))}
                </ul>
              </Section>

              <Section icon={CalendarClock} title="Échéances à venir" testid="admin-user-sheet-deadlines">
                {d.deadlines.length === 0 ? (
                  <p className="text-xs text-slate-400">Aucune échéance en attente.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {d.deadlines.map((dl, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-600">{dl.title}</span>
                        <span className="shrink-0 font-bold text-slate-500">{fmtDate(dl.date, { day: "numeric", month: "short", year: "numeric" })}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section icon={Award} title="Examen civique & quiz" testid="admin-user-sheet-quiz">
                <Row label="Tentatives terminées" value={String(d.quiz.completed)} />
                <Row label="Examens blancs réussis" value={String(d.quiz.exams_passed)} />
                <Row label="Meilleur score" value={d.quiz.best_percentage != null ? `${d.quiz.best_percentage} %` : null} />
                {d.quiz.last_attempts.length > 0 && (
                  <ul className="mt-1 space-y-1">
                    {d.quiz.last_attempts.map((a, i) => (
                      <li key={i} className="flex items-center justify-between text-[11px] text-slate-500">
                        <span className="capitalize">{a.mode} · {a.score}/{a.total}</span>
                        <span className={a.passed ? "font-bold text-emeraude" : "font-bold text-rouge"}>
                          {a.passed ? "Réussi" : "Échoué"} · {fmtDate(a.finished_at, { day: "numeric", month: "short" })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section icon={ImageIcon} title={`Documents fournis — Interprète IA (${d.uploads.length})`} testid="admin-user-sheet-uploads">
                {d.uploads.length === 0 ? (
                  <p className="text-xs text-slate-400">Aucun document envoyé à l'interprète.</p>
                ) : (
                  <ul className="space-y-2">
                    {d.uploads.map((u) => (
                      <li key={u.id} className="rounded-lg border border-slate-100 p-2.5" data-testid="admin-user-upload-item">
                        <div className="flex items-center justify-between gap-2">
                          <span className="min-w-0 truncate text-xs font-bold text-slate-700">{u.filename || "Document"}</span>
                          <a href={`${API_BASE}/admin/users/${userId}/documents/${u.id}/image`} target="_blank" rel="noreferrer"
                            className="flex shrink-0 items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[10px] font-bold text-bleu hover:border-bleu/40"
                            data-testid="admin-user-upload-view">
                            <ExternalLink className="h-3 w-3" /> Voir
                          </a>
                        </div>
                        <p className="mt-0.5 text-[10px] text-slate-400">
                          {fmtDate(u.created_at, { day: "numeric", month: "short", year: "numeric" })}
                          {u.target && ` · traduit en ${u.target}`}
                          {u.size && ` · ${Math.round(u.size / 1024)} Ko`}
                        </p>
                        {u.translation && <p className="mt-1 line-clamp-2 text-[11px] text-slate-500">{u.translation}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section icon={FileText} title={`Courriers édités par l'IA (${d.courriers.length})`} testid="admin-user-sheet-courriers">
                {d.courriers.length === 0 ? (
                  <p className="text-xs text-slate-400">Aucun courrier généré.</p>
                ) : (
                  <ul className="space-y-2">
                    {d.courriers.map((c) => (
                      <li key={c.id} className="rounded-lg border border-slate-100 p-2.5" data-testid="admin-user-courrier-item">
                        <div className="flex items-center justify-between gap-2">
                          <span className="min-w-0 truncate text-xs font-bold text-slate-700">{c.objet || "Courrier"}</span>
                          <span className="shrink-0 text-[10px] text-slate-400">{fmtDate(c.created_at, { day: "numeric", month: "short" })}</span>
                        </div>
                        {c.situation && <p className="mt-1 line-clamp-2 text-[11px] text-slate-500">{c.situation}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section icon={Bot} title={`Activité IA & plateforme — ${d.ai.total} message(s)`} testid="admin-user-sheet-activity">
                {d.ai.by_agent.map((a) => (
                  <Row key={a.agent} label={AGENT_LABELS[a.agent] || a.agent}
                    value={`${a.count} msg · ${fmtDate(a.last, { day: "numeric", month: "short" })}`} />
                ))}
                <Row label="Candidatures suivies" value={String(d.activity.applications)} />
                <Row label="Suggestions envoyées" value={String(d.activity.suggestions)} />
              </Section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
